import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import EmptyState from "@/components/base/EmptyState";
import AdminPageHeader from "../components/AdminPageHeader";
import AdminToolbar from "../components/AdminToolbar";
import { useAuth } from "@/context/AuthContext";
import { getAdminBusinesses, adminAction } from "@/services/adminApi";
import type { AdminBusiness } from "@/types";

export default function AdminFeatured() {
  const { token } = useAuth();
  const [rows, setRows] = useState<AdminBusiness[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getAdminBusinesses(token)
      .then((b) => active && setRows(b))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const featured = useMemo(() => rows.filter((r) => r.isFeatured), [rows]);
  const candidates = useMemo(() => rows.filter((r) => !r.isFeatured && r.status === "approved"), [rows]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return featured.filter(
      (r) => !q || r.name.toLowerCase().includes(q) || r.categoryName.toLowerCase().includes(q) || r.locationName.toLowerCase().includes(q),
    );
  }, [featured, query]);

  const remove = async (id: string) => {
    const ok = await adminAction(`/admin/businesses/${id}/feature`, { featured: false }, token);
    if (!ok) {
      setActionError("Couldn't update featured placements. Please try again.");
      setTimeout(() => setActionError(""), 3200);
      return;
    }
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, isFeatured: false } : r)));
    setNotice("Removed from featured placements.");
    setTimeout(() => setNotice(""), 2400);
  };

  const add = async (id: string) => {
    const ok = await adminAction(`/admin/businesses/${id}/feature`, { featured: true }, token);
    if (!ok) {
      setActionError("Couldn't update featured placements. Please try again.");
      setTimeout(() => setActionError(""), 3200);
      return;
    }
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, isFeatured: true } : r)));
    setNotice("Added to featured placements.");
    setTimeout(() => setNotice(""), 2400);
  };

  return (
    <>
      <Seo title="Featured Listings" path="/admin/featured" />

      <AdminPageHeader
        icon="ri-vip-crown-line"
        title="Featured listings"
        subtitle="Businesses promoted across the homepage, category and location pages."
        actions={
          actionError ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-2 text-sm text-secondary-900">
              <i className="ri-error-warning-line text-secondary-600"></i> {actionError}
            </span>
          ) : notice ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
            </span>
          ) : undefined
        }
      />

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Featured now</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-foreground-950">{featured.length}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Eligible to feature</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-accent-700">{candidates.length}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Homepage slots</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-foreground-950">8</p>
        </div>
      </div>

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search featured listings…" />
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="h-72 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <EmptyState icon="ri-wifi-off-line" title="Couldn't load featured listings" description="Please refresh and try again." />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-vip-crown-line" title="No featured listings" description="Feature an approved business to spotlight it across the site." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((r) => (
              <article key={r.id} className="flex flex-col rounded-lg border border-primary-200 bg-primary-50/50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className="ri-vip-crown-fill"></i>
                  </span>
                  <StatusBadge status={r.status} />
                </div>
                <h2 className="mt-3 font-heading text-base font-bold text-foreground-950">{r.name}</h2>
                <p className="mt-1 text-xs text-foreground-600">
                  {r.categoryName} · {r.locationName}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1"><i className="ri-eye-line"></i> {r.views.toLocaleString()}</span>
                  <span className="inline-flex items-center gap-1"><i className="ri-star-line"></i> {r.rating || "—"}</span>
                  <span className="inline-flex items-center gap-1"><i className="ri-vip-crown-line"></i> {r.plan}</span>
                </div>
                <div className="mt-auto flex items-center gap-2 border-t border-primary-200/60 pt-3 mt-4">
                  <Link
                    to={`/business/${r.slug}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-external-link-line"></i> View
                  </Link>
                  <button
                    type="button"
                    onClick={() => remove(r.id)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-secondary-300 px-3 py-1.5 text-xs font-semibold text-secondary-800 hover:bg-secondary-50 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-close-line"></i> Unfeature
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {!loading && !error && candidates.length > 0 && (
        <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <h2 className="font-heading text-base font-bold text-foreground-950">Suggest a listing to feature</h2>
          <p className="mt-0.5 text-xs text-foreground-500">Approved businesses not currently promoted.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {candidates.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => add(c.id)}
                className="inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-medium text-foreground-700 hover:border-primary-400 hover:bg-primary-50 cursor-pointer"
              >
                <i className="ri-add-line text-primary-600"></i>
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}